import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  ExternalLink, CheckCircle, Clock, AlertCircle, Copy, 
  ChevronDown, ChevronUp, Search, Filter, RefreshCw,
  Globe, Building2, Stethoscope, MapPin, Zap, Play, Loader2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// CopyButton component moved outside to avoid re-creation on every render
const CopyButton = ({ text, label, copied, onCopy }) => (
  <button
    onClick={() => onCopy(text, label)}
    className={`p-1 rounded hover:bg-gray-200 ${copied === label ? 'text-green-600' : 'text-gray-500'}`}
    title="Copy to clipboard"
  >
    <Copy className="w-4 h-4" />
  </button>
);

const CitationBuilder = () => {
  const [directories, setDirectories] = useState([]);
  const [stats, setStats] = useState(null);
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [copied, setCopied] = useState('');
  const [actionPlan, setActionPlan] = useState(null);
  const [showActionPlan, setShowActionPlan] = useState(true);
  
  // Auto-submission state
  const [autoStatus, setAutoStatus] = useState(null);
  const [selectedForAuto, setSelectedForAuto] = useState([]);
  const [availableAuto, setAvailableAuto] = useState([]);
  const [autoRunning, setAutoRunning] = useState(false);
  
  // Aggregator API state
  const [aggregatorStatus, setAggregatorStatus] = useState(null);
  const [aggregatorResults, setAggregatorResults] = useState({});
  const [aggregatorLoading, setAggregatorLoading] = useState({});
  const [activeTab, setActiveTab] = useState('directories'); // 'directories', 'aggregators', 'automation', 'brightlocal', 'submissions'
  
  // BrightLocal state
  const [blStatus, setBlStatus] = useState(null);
  const [blCampaign, setBlCampaign] = useState(null);
  const [blLookup, setBlLookup] = useState(null);
  const [blLoading, setBlLoading] = useState(false);
  
  // Submission engine state
  const [submissions, setSubmissions] = useState(null);
  const [subLoading, setSubLoading] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [napData, setNapData] = useState(null);

  const fetchAggregatorStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/citation-aggregators/status`);
      if (res.ok) {
        const data = await res.json();
        setAggregatorStatus(data);
      }
    } catch (err) {
      console.error('Error fetching aggregator status:', err);
    }
  };

  const testAggregatorSubmission = async (aggregator) => {
    setAggregatorLoading(prev => ({ ...prev, [aggregator]: true }));
    try {
      const res = await fetch(`${API_URL}/api/citation-aggregators/submit/${aggregator}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ aggregator, test_mode: true })
      });
      if (res.ok) {
        const data = await res.json();
        setAggregatorResults(prev => ({ ...prev, [aggregator]: data }));
      }
    } catch (err) {
      console.error(`Error testing ${aggregator}:`, err);
      setAggregatorResults(prev => ({ ...prev, [aggregator]: { success: false, message: err.message } }));
    } finally {
      setAggregatorLoading(prev => ({ ...prev, [aggregator]: false }));
    }
  };

  const fetchAvailableAuto = async () => {
    try {
      const res = await fetch(`${API_URL}/api/citation-auto/available`);
      if (res.ok) {
        const data = await res.json();
        setAvailableAuto(data.directories || []);
      }
    } catch (err) {
      console.error('Error fetching available auto directories:', err);
    }
  };

  const fetchAutoStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/citation-auto/status`);
      if (res.ok) {
        const data = await res.json();
        setAutoStatus(data);
        if (!data.running) {
          setAutoRunning(false);
        }
      }
    } catch (err) {
      console.error('Error fetching auto status:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchActionPlan();
    fetchAvailableAuto();
    fetchAggregatorStatus();
    fetchBrightLocal();
    fetchSubmissions();
  }, []);

  const fetchBrightLocal = async () => {
    setBlLoading(true);
    try {
      const [statusRes, campaignRes, lookupRes] = await Promise.all([
        fetch(`${API_URL}/api/brightlocal/status`).catch(() => null),
        fetch(`${API_URL}/api/brightlocal/citation-builder/campaign`).catch(() => null),
        fetch(`${API_URL}/api/brightlocal/citation-builder/lookup`).catch(() => null),
      ]);
      if (statusRes?.ok) setBlStatus(await statusRes.json());
      if (campaignRes?.ok) setBlCampaign(await campaignRes.json());
      if (lookupRes?.ok) setBlLookup(await lookupRes.json());
    } catch (err) {
      console.error('BrightLocal fetch error:', err);
    }
    setBlLoading(false);
  };

  const fetchSubmissions = async () => {
    setSubLoading(true);
    try {
      const [dirRes, napRes] = await Promise.all([
        fetch(`${API_URL}/api/citation-submit/directories`),
        fetch(`${API_URL}/api/citation-submit/nap`),
      ]);
      if (dirRes.ok) setSubmissions(await dirRes.json());
      if (napRes.ok) setNapData(await napRes.json());
    } catch (err) {
      console.error('Submissions fetch error:', err);
    }
    setSubLoading(false);
  };

  const updateSubmissionStatus = async (directoryId, status, listingUrl = null) => {
    try {
      const res = await fetch(`${API_URL}/api/citation-submit/update-status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ directory_id: directoryId, status, listing_url: listingUrl }),
      });
      if (res.ok) {
        fetchSubmissions();
      }
    } catch (err) {
      console.error('Update status error:', err);
    }
  };

  // Poll for automation status when running
  useEffect(() => {
    let interval;
    if (autoRunning) {
      interval = setInterval(fetchAutoStatus, 2000);
    }
    return () => clearInterval(interval);
  }, [autoRunning, fetchAutoStatus]);

  const startAutoSubmit = async (directories) => {
    try {
      const res = await fetch(`${API_URL}/api/citation-auto/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          directories: directories,
          headless: false // Show browser window
        })
      });
      if (res.ok) {
        setAutoRunning(true);
        fetchAutoStatus();
      }
    } catch (err) {
      console.error('Error starting auto submission:', err);
      alert('Error starting automation. Check console for details.');
    }
  };

  const startAutoSubmitAll = async () => {
    try {
      const res = await fetch(`${API_URL}/api/citation-auto/submit-all`, {
        method: 'POST'
      });
      if (res.ok) {
        setAutoRunning(true);
        fetchAutoStatus();
      }
    } catch (err) {
      console.error('Error starting auto submission:', err);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/citations-enhanced/directories`);
      if (res.ok) {
        const data = await res.json();
        setDirectories(data.directories || []);
        setStats(data.stats);
        setBusinessData(data.business);
      }
    } catch (err) {
      console.error('Error fetching citations:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchActionPlan = async () => {
    try {
      const res = await fetch(`${API_URL}/api/citations-enhanced/action-plan`);
      if (res.ok) {
        const data = await res.json();
        setActionPlan(data.action_plan);
      }
    } catch (err) {
      console.error('Error fetching action plan:', err);
    }
  };

  const updateStatus = async (directoryId, newStatus, listingUrl = null) => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      if (listingUrl) params.append('listing_url', listingUrl);
      
      const res = await fetch(
        `${API_URL}/api/citations-enhanced/directories/${directoryId}/status?${params}`,
        { method: 'PUT' }
      );
      if (res.ok) {
        fetchData();
      }
    } catch (err) {
      console.error('Error updating status:', err);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(''), 2000);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'live': return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'submitted': return <Clock className="w-5 h-5 text-blue-500" />;
      case 'error': return <AlertCircle className="w-5 h-5 text-red-500" />;
      default: return <Clock className="w-5 h-5 text-yellow-500" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      live: 'bg-green-100 text-green-800',
      submitted: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      error: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.pending;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'aggregator': return <Zap className="w-4 h-4" />;
      case 'search_engine': return <Search className="w-4 h-4" />;
      case 'healthcare': 
      case 'dental': return <Stethoscope className="w-4 h-4" />;
      case 'local': return <MapPin className="w-4 h-4" />;
      default: return <Globe className="w-4 h-4" />;
    }
  };

  const filteredDirectories = directories.filter(d => {
    if (filter !== 'all' && d.status !== filter) return false;
    if (categoryFilter !== 'all' && d.category !== categoryFilter) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Citation Builder | Admin Dashboard</title>
      </Helmet>

      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="w-6 h-6 text-teal-600" />
            Citation Builder
          </h1>
          <p className="text-gray-600 mt-1">
            Submit business listings to 50+ directories - Data pre-filled for Fort Worth Dental
          </p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-green-600">{stats.live}</p>
              <p className="text-sm text-gray-600">Live</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-yellow-600">{stats.pending}</p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-blue-600">{stats.submitted}</p>
              <p className="text-sm text-gray-600">Submitted</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-gray-800">{stats.total}</p>
              <p className="text-sm text-gray-600">Total</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-purple-600">{stats.aggregators}</p>
              <p className="text-sm text-gray-600">Aggregators</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4 text-center">
              <p className="text-3xl font-bold text-teal-600">{stats.downstream_reach}+</p>
              <p className="text-sm text-gray-600">Downstream Sites</p>
            </div>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6 border-b">
          <button
            onClick={() => setActiveTab('directories')}
            className={`px-4 py-2 font-medium rounded-t-lg transition ${
              activeTab === 'directories' 
                ? 'bg-white text-teal-700 border-t border-l border-r -mb-px' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            📋 Directories ({directories.length})
          </button>
          <button
            onClick={() => setActiveTab('aggregators')}
            className={`px-4 py-2 font-medium rounded-t-lg transition ${
              activeTab === 'aggregators' 
                ? 'bg-white text-purple-700 border-t border-l border-r -mb-px' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            🌐 Aggregator APIs
          </button>
          <button
            onClick={() => setActiveTab('automation')}
            className={`px-4 py-2 font-medium rounded-t-lg transition ${
              activeTab === 'automation' 
                ? 'bg-white text-indigo-700 border-t border-l border-r -mb-px' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            Browser Automation ({availableAuto.length})
          </button>
          <button
            onClick={() => setActiveTab('brightlocal')}
            className={`px-4 py-2 font-medium rounded-t-lg transition ${
              activeTab === 'brightlocal' 
                ? 'bg-white text-green-700 border-t border-l border-r -mb-px' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="brightlocal-tab"
          >
            BrightLocal {blStatus?.connected ? '(Connected)' : ''}
          </button>
          <button
            onClick={() => setActiveTab('submissions')}
            className={`px-4 py-2 font-medium rounded-t-lg transition ${
              activeTab === 'submissions' 
                ? 'bg-white text-teal-700 border-t border-l border-r -mb-px' 
                : 'text-gray-600 hover:text-gray-800'
            }`}
            data-testid="submissions-tab"
          >
            Submit Citations ({submissions?.submitted || 0}/{submissions?.total || 0})
          </button>
        </div>

        {/* Aggregator APIs Tab */}
        {activeTab === 'aggregators' && (
          <div className="space-y-6 mb-6">
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6">
              <h2 className="text-xl font-bold text-purple-800 mb-2">🌐 Data Aggregator API Integrations</h2>
              <p className="text-gray-600 mb-4">
                Submit your NAP directly to major data aggregators via API. These aggregators distribute to 100+ downstream directories automatically.
              </p>
              
              {aggregatorStatus?.aggregators && (
                <div className="grid md:grid-cols-3 gap-4">
                  {Object.entries(aggregatorStatus.aggregators).map(([key, agg]) => (
                    <div key={key} className="bg-white rounded-lg shadow p-4 border">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-bold text-gray-800">{agg.name}</h3>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          agg.configured ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {agg.configured ? '✓ API Connected' : '⚠ Needs API Key'}
                        </span>
                      </div>
                      
                      <p className="text-sm text-gray-600 mb-2">
                        <strong>Cost:</strong> {agg.cost}
                      </p>
                      
                      <div className="text-xs text-gray-500 mb-3">
                        <strong>Feeds to:</strong> {agg.feeds_to?.slice(0, 5).join(', ')}
                        {agg.feeds_to?.length > 5 && ` +${agg.feeds_to.length - 5} more`}
                      </div>
                      
                      <div className="flex gap-2">
                        <button
                          onClick={() => testAggregatorSubmission(key)}
                          disabled={aggregatorLoading[key]}
                          className="flex-1 bg-purple-600 text-white px-3 py-2 rounded text-sm font-medium hover:bg-purple-700 disabled:bg-gray-300 transition"
                        >
                          {aggregatorLoading[key] ? (
                            <span className="flex items-center justify-center gap-1">
                              <Loader2 className="w-3 h-3 animate-spin" /> Testing...
                            </span>
                          ) : '🧪 Test Submit'}
                        </button>
                        <a
                          href={agg.signup_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-gray-100 text-gray-700 px-3 py-2 rounded text-sm hover:bg-gray-200 transition"
                        >
                          Get API Key →
                        </a>
                      </div>
                      
                      {/* Test Results */}
                      {aggregatorResults[key] && (
                        <div className={`mt-3 p-3 rounded text-sm ${
                          aggregatorResults[key].success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                        }`}>
                          <div className="font-medium mb-1">
                            {aggregatorResults[key].success ? '✅ ' : '❌ '}
                            {aggregatorResults[key].status?.toUpperCase()}
                          </div>
                          <p className="text-gray-600">{aggregatorResults[key].message}</p>
                          {aggregatorResults[key].next_steps && (
                            <ul className="mt-2 text-xs list-disc list-inside text-gray-500">
                              {aggregatorResults[key].next_steps.map((step, i) => (
                                <li key={i}>{step}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              
              {/* API Keys Info */}
              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-semibold text-blue-800 mb-2">🔑 How to Add API Keys</h4>
                <p className="text-sm text-gray-600 mb-2">
                  Add your API keys to the backend <code className="bg-blue-100 px-1 rounded">.env</code> file:
                </p>
                <pre className="bg-gray-800 text-green-400 p-3 rounded text-xs overflow-x-auto">
{`DATA_AXLE_API_KEY=your_data_axle_key_here
FOURSQUARE_API_KEY=your_foursquare_key_here
LOCALEZE_API_KEY=your_localeze_key_here`}
                </pre>
                <p className="text-xs text-gray-500 mt-2">
                  After adding keys, restart the backend service and refresh this page.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Browser Automation Tab */}
        {activeTab === 'automation' && (
          <div className="space-y-6 mb-6">
            {/* Auto-Submit Section */}
        <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-purple-800 flex items-center gap-2">
              🤖 Auto-Submit Citations
              {autoRunning && (
                <span className="flex items-center gap-1 text-sm bg-purple-200 text-purple-800 px-2 py-0.5 rounded-full">
                  <Loader2 className="w-3 h-3 animate-spin" />
                  Running...
                </span>
              )}
            </h3>
            <div className="flex gap-2">
              <button
                onClick={() => startAutoSubmit(['yellowpages', 'foursquare', 'healthgrades', 'bbb', 'manta'])}
                disabled={autoRunning}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  autoRunning 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                <Play className="w-4 h-4" />
                Quick Start (Top 5)
              </button>
              <button
                onClick={startAutoSubmitAll}
                disabled={autoRunning}
                className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition ${
                  autoRunning 
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                    : 'bg-indigo-600 text-white hover:bg-indigo-700'
                }`}
              >
                <Zap className="w-4 h-4" />
                Auto-Submit All ({availableAuto.length})
              </button>
            </div>
          </div>
          
          {/* Progress */}
          {autoStatus && autoRunning && (
            <div className="mb-3">
              <div className="flex justify-between text-sm text-purple-700 mb-1">
                <span>Progress: {autoStatus.progress}/{autoStatus.total}</span>
                <span>Current: {autoStatus.current_directory}</span>
              </div>
              <div className="w-full bg-purple-200 rounded-full h-2">
                <div 
                  className="bg-purple-600 h-2 rounded-full transition-all"
                  style={{ width: `${(autoStatus.progress / autoStatus.total) * 100}%` }}
                />
              </div>
            </div>
          )}

          {/* Results */}
          {autoStatus?.results?.length > 0 && (
            <div className="mt-3">
              <h4 className="text-sm font-medium text-purple-800 mb-2">Results:</h4>
              <div className="max-h-40 overflow-y-auto space-y-1">
                {autoStatus.results.map((result, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm bg-white p-2 rounded">
                    {result.status === 'partial' && <Clock className="w-4 h-4 text-yellow-500" />}
                    {result.status === 'success' && <CheckCircle className="w-4 h-4 text-green-500" />}
                    {result.status === 'failed' && <AlertCircle className="w-4 h-4 text-red-500" />}
                    <span className="font-medium">{result.directory_name}:</span>
                    <span className="text-gray-600 truncate">{result.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-purple-600 mt-2">
            💡 Automation opens each directory in a browser, pre-fills forms, and guides you through submission.
            Some sites require manual verification (phone/email).
          </p>
        </div>
          </div>
        )}

        {/* Submissions Tab */}
        {activeTab === 'submissions' && (
          <div className="space-y-6 mb-6" data-testid="submissions-section">
            {/* NAP Data Copy Panel */}
            {napData && (
              <div className="border rounded-lg bg-teal-50 border-teal-200 p-5">
                <h3 className="font-bold text-gray-800 mb-3">Your Business NAP — Copy & Paste</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { label: 'Business Name', value: napData.name },
                    { label: 'Phone', value: napData.phone },
                    { label: 'Address', value: `${napData.address}, ${napData.city}, ${napData.state} ${napData.zip}` },
                    { label: 'Website', value: napData.website },
                  ].map(({ label, value }) => (
                    <button
                      key={label}
                      onClick={() => copyToClipboard(value, label)}
                      className={`text-left p-3 rounded-lg border transition ${
                        copiedField === label ? 'bg-green-100 border-green-400' : 'bg-white border-gray-200 hover:border-teal-400'
                      }`}
                    >
                      <p className="text-xs text-gray-500">{label}</p>
                      <p className="font-medium text-gray-900 text-sm truncate">{value}</p>
                      <p className="text-xs mt-1 text-teal-600">{copiedField === label ? 'Copied!' : 'Click to copy'}</p>
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => copyToClipboard(napData.description, 'Description')}
                  className={`mt-3 w-full text-left p-3 rounded-lg border transition ${
                    copiedField === 'Description' ? 'bg-green-100 border-green-400' : 'bg-white border-gray-200 hover:border-teal-400'
                  }`}
                >
                  <p className="text-xs text-gray-500">Business Description</p>
                  <p className="text-sm text-gray-700 line-clamp-2">{napData.description}</p>
                  <p className="text-xs mt-1 text-teal-600">{copiedField === 'Description' ? 'Copied!' : 'Click to copy'}</p>
                </button>
              </div>
            )}

            {/* Stats */}
            {submissions && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-gray-800">{submissions.total}</p>
                  <p className="text-sm text-gray-500">Total Directories</p>
                </div>
                <div className="bg-white border rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">{submissions.submitted}</p>
                  <p className="text-sm text-gray-500">Submitted</p>
                </div>
                <div className="bg-white border rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-amber-500">{submissions.pending}</p>
                  <p className="text-sm text-gray-500">Pending</p>
                </div>
                <div className="bg-white border rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-red-500">{submissions.nap_issues}</p>
                  <p className="text-sm text-gray-500">NAP Issues</p>
                </div>
              </div>
            )}

            {/* Directory List */}
            {submissions?.directories?.map((dir, idx) => {
              const statusColors = {
                not_started: 'bg-gray-100 text-gray-600',
                submitted: 'bg-blue-100 text-blue-700',
                verified: 'bg-green-100 text-green-700',
                live: 'bg-green-200 text-green-800',
                needs_update: 'bg-red-100 text-red-700',
                brightlocal: 'bg-purple-100 text-purple-700',
              };
              const authorityColors = {
                'Very High': 'text-red-600 bg-red-50',
                'High': 'text-orange-600 bg-orange-50',
                'Medium': 'text-yellow-600 bg-yellow-50',
                'Low': 'text-gray-500 bg-gray-50',
              };
              const methodLabels = {
                api: 'Auto (API)',
                manual: 'Manual',
                brightlocal: 'BrightLocal',
              };
              return (
                <div key={dir.id} className="border rounded-lg bg-white overflow-hidden" data-testid={`sub-dir-${dir.id}`}>
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-3 flex-1">
                      <div className={`w-2 h-12 rounded-full ${
                        dir.status === 'live' || dir.status === 'verified' ? 'bg-green-500' :
                        dir.status === 'submitted' ? 'bg-blue-500' :
                        dir.status === 'needs_update' ? 'bg-red-500' :
                        'bg-gray-300'
                      }`} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-gray-900">{dir.name}</span>
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${authorityColors[dir.authority]}`}>
                            {dir.authority}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {methodLabels[dir.method]}
                          </span>
                          <span className="text-xs text-gray-400">{dir.category}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{dir.notes}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <span className={`text-xs px-3 py-1 rounded-full font-medium ${statusColors[dir.status]}`}>
                        {dir.status === 'not_started' ? 'Not Started' : dir.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                      {dir.submit_url && dir.status === 'not_started' && (
                        <a
                          href={dir.submit_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-3 py-1.5 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 transition whitespace-nowrap"
                          onClick={() => updateSubmissionStatus(dir.id, 'submitted')}
                        >
                          Submit Now
                        </a>
                      )}
                      {dir.status === 'not_started' && dir.method === 'brightlocal' && (
                        <button
                          onClick={() => updateSubmissionStatus(dir.id, 'brightlocal')}
                          className="px-3 py-1.5 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition whitespace-nowrap"
                        >
                          Send to BL
                        </button>
                      )}
                      {dir.status === 'submitted' && (
                        <button
                          onClick={() => updateSubmissionStatus(dir.id, 'verified')}
                          className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition whitespace-nowrap"
                        >
                          Mark Verified
                        </button>
                      )}
                      {dir.listing_url && (
                        <a href={dir.listing_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* BrightLocal Tab */}
        {activeTab === 'brightlocal' && (
          <div className="space-y-6 mb-6" data-testid="brightlocal-section">
            {/* Connection Status Header */}
            <div className={`border rounded-lg p-6 ${blStatus?.connected ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-800">Citation Health Dashboard</h2>
                <button onClick={fetchBrightLocal} className="px-3 py-1.5 bg-white border rounded-lg text-sm hover:bg-gray-50 flex items-center gap-1" data-testid="bl-refresh">
                  <RefreshCw className={`w-4 h-4 ${blLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
              {blStatus?.connected ? (
                <div className="grid md:grid-cols-4 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Business</p>
                    <p className="font-bold text-gray-900">{blStatus.business_name}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Location ID</p>
                    <p className="font-bold text-gray-900">{blStatus.location_id}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Address</p>
                    <p className="font-bold text-gray-900 text-sm">{blStatus.address?.address1}, {blStatus.address?.city}, {blStatus.address?.region_code}</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <p className="text-xs text-gray-500 mb-1">Campaign</p>
                    <p className="font-bold text-green-700">#{blStatus.campaign_id}</p>
                  </div>
                </div>
              ) : (
                <p className="text-red-700">Not connected. Check BrightLocal API key.</p>
              )}
            </div>

            {/* Citation Stats Summary */}
            {blLookup?.success && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white border rounded-lg p-6 text-center" data-testid="found-citations-count">
                  <p className="text-4xl font-bold text-green-600">{blLookup.lookup?.citations?.length || 0}</p>
                  <p className="text-gray-600 mt-1">Citations Found</p>
                  <p className="text-xs text-gray-400">Directories where FWD is listed</p>
                </div>
                <div className="bg-white border rounded-lg p-6 text-center" data-testid="nap-issues-count">
                  <p className="text-4xl font-bold text-amber-500">
                    {blLookup.lookup?.citations?.filter(c => {
                      const nap = c.nap;
                      if (!nap) return true;
                      const correctPhone = '8179853337';
                      const phone = (nap.phone || '').replace(/\D/g, '');
                      return !phone.endsWith(correctPhone);
                    }).length || 0}
                  </p>
                  <p className="text-gray-600 mt-1">NAP Issues</p>
                  <p className="text-xs text-gray-400">Incorrect phone or address</p>
                </div>
                <div className="bg-white border rounded-lg p-6 text-center" data-testid="lookup-status">
                  <p className="text-4xl font-bold text-blue-600 capitalize">{blLookup.lookup?.lookup_status || '?'}</p>
                  <p className="text-gray-600 mt-1">Lookup Status</p>
                  <p className="text-xs text-gray-400">{blLookup.lookup?.lookup_completed_at || 'Scanning...'}</p>
                </div>
              </div>
            )}

            {/* Found Citations with NAP Analysis */}
            {blLookup?.success && blLookup.lookup?.citations?.length > 0 && (
              <div className="border rounded-lg bg-white overflow-hidden" data-testid="citation-list">
                <div className="p-4 border-b bg-gray-50">
                  <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    Found Citations — NAP Accuracy Check
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Correct phone: <span className="font-mono font-bold">(817) 985-3337</span> | 
                    Address: <span className="font-mono font-bold">4620 Citylake Blvd W</span>
                  </p>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Directory</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Listed Name</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Phone</th>
                        <th className="text-left py-3 px-4 font-medium text-gray-600">Address</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">NAP Status</th>
                        <th className="text-center py-3 px-4 font-medium text-gray-600">Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {blLookup.lookup.citations.map((cit, idx) => {
                        const nap = cit.nap || {};
                        const correctPhone = '8179853337';
                        const listedPhone = (nap.phone || '').replace(/\D/g, '');
                        const phoneMatch = listedPhone.endsWith(correctPhone);
                        const nameMatch = (nap.name || '').toLowerCase().includes('fort worth dental');
                        const allGood = phoneMatch && nameMatch && nap.address;
                        
                        return (
                          <tr key={idx} className={`border-b hover:bg-gray-50 ${!allGood ? 'bg-red-50' : ''}`} data-testid={`citation-row-${idx}`}>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-blue-500 flex-shrink-0" />
                                <span className="font-medium text-gray-900">{cit.domain}</span>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <span className={nameMatch ? 'text-gray-700' : 'text-red-600 font-medium'}>
                                {nap.name || 'N/A'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`font-mono ${phoneMatch ? 'text-green-700' : 'text-red-600 font-bold'}`}>
                                {nap.phone || 'N/A'}
                              </span>
                              {!phoneMatch && nap.phone && (
                                <span className="block text-xs text-red-500 mt-0.5">Wrong number</span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-gray-600 max-w-[200px] truncate">
                              {nap.address || 'N/A'}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {allGood ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <CheckCircle className="w-3 h-3" /> Accurate
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                  <AlertCircle className="w-3 h-3" /> Needs Fix
                                </span>
                              )}
                            </td>
                            <td className="py-3 px-4 text-center">
                              {cit.profile_url && (
                                <a href={cit.profile_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800">
                                  <ExternalLink className="w-4 h-4 inline" />
                                </a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Missing Directories — Where to Build */}
            <div className="border rounded-lg bg-white p-6" data-testid="missing-directories">
              <h3 className="text-lg font-bold text-gray-800 mb-3 flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                Key Missing Directories
              </h3>
              <p className="text-sm text-gray-500 mb-4">
                These high-authority directories were NOT found in the lookup. Submitting here will improve local SEO.
              </p>
              {(() => {
                const foundDomains = new Set((blLookup?.lookup?.citations || []).map(c => c.domain?.toLowerCase()));
                const keyDirectories = [
                  { name: 'Facebook', domain: 'facebook.com', authority: 'Very High', category: 'Social' },
                  { name: 'Bing Places', domain: 'bing.com', authority: 'Very High', category: 'Search Engine' },
                  { name: 'Healthgrades', domain: 'healthgrades.com', authority: 'Very High', category: 'Healthcare' },
                  { name: 'Zocdoc', domain: 'zocdoc.com', authority: 'High', category: 'Healthcare' },
                  { name: 'WebMD', domain: 'webmd.com', authority: 'Very High', category: 'Healthcare' },
                  { name: 'Vitals', domain: 'vitals.com', authority: 'High', category: 'Healthcare' },
                  { name: 'RateMDs', domain: 'ratemds.com', authority: 'High', category: 'Healthcare' },
                  { name: 'Dentistry.com', domain: 'dentistry.com', authority: 'High', category: 'Dental' },
                  { name: 'BBB', domain: 'bbb.org', authority: 'Very High', category: 'Business' },
                  { name: 'YellowPages', domain: 'yellowpages.com', authority: 'High', category: 'Directory' },
                  { name: 'Superpages', domain: 'superpages.com', authority: 'Medium', category: 'Directory' },
                  { name: 'Foursquare', domain: 'foursquare.com', authority: 'Medium', category: 'Directory' },
                  { name: 'MapQuest', domain: 'mapquest.com', authority: 'Medium', category: 'Maps' },
                  { name: 'CitySearch', domain: 'citysearch.com', authority: 'Medium', category: 'Directory' },
                  { name: 'Alignable', domain: 'alignable.com', authority: 'Medium', category: 'Business' },
                  { name: 'Nextdoor', domain: 'nextdoor.com', authority: 'High', category: 'Local' },
                  { name: 'DexKnows', domain: 'dexknows.com', authority: 'Medium', category: 'Directory' },
                  { name: 'Angi', domain: 'angi.com', authority: 'High', category: 'Services' },
                  { name: 'Thumbtack', domain: 'thumbtack.com', authority: 'Medium', category: 'Services' },
                  { name: 'Instagram', domain: 'instagram.com', authority: 'Very High', category: 'Social' },
                ];
                const missing = keyDirectories.filter(d => !foundDomains.has(d.domain));
                const found = keyDirectories.filter(d => foundDomains.has(d.domain));
                
                return (
                  <div className="space-y-3">
                    {missing.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {missing.map((dir, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100 rounded-lg">
                            <div className="flex items-center gap-2">
                              <Globe className="w-4 h-4 text-amber-600" />
                              <div>
                                <span className="font-medium text-gray-800 text-sm">{dir.name}</span>
                                <span className="text-xs text-gray-400 ml-2">{dir.category}</span>
                              </div>
                            </div>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              dir.authority === 'Very High' ? 'bg-red-100 text-red-700' :
                              dir.authority === 'High' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }`}>
                              {dir.authority}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                    {found.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm text-green-700 font-medium mb-2">Already listed on:</p>
                        <div className="flex flex-wrap gap-2">
                          {found.map((dir, idx) => (
                            <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 text-green-700 rounded-full text-xs">
                              <CheckCircle className="w-3 h-3" /> {dir.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>

            {/* Campaign Builder Section */}
            {blCampaign?.success && (
              <div className="border rounded-lg p-6 bg-white">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-600" />
                  Citation Builder Campaign #{blCampaign.campaign?.campaign_id}
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Lookup Status</p>
                    <p className="font-bold text-purple-700 capitalize">{blCampaign.campaign?.lookup_status || 'Unknown'}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Citations Ordered</p>
                    <p className="font-bold text-purple-700">{blCampaign.campaign?.campaigns?.[0]?.citations_ordered || 0}</p>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-500">Campaign Status</p>
                    <p className="font-bold text-purple-700 capitalize">{blCampaign.campaign?.campaigns?.[0]?.status || 'Saved'}</p>
                  </div>
                </div>
                {blCampaign.campaign?.campaigns?.[0]?.status === 'saved' && (
                  <p className="text-sm text-yellow-700 bg-yellow-50 p-3 rounded-lg mt-4">
                    Campaign is ready to confirm. Log into <a href="https://app.brightlocal.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">BrightLocal</a> to review and confirm submissions, or order additional citations.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Directories Tab */}
        {activeTab === 'directories' && (
          <>
        {/* Business Data Card - Collapsible */}
        {businessData && (
          <div className="bg-gradient-to-r from-teal-50 to-blue-50 border border-teal-200 rounded-lg p-4 mb-6">
            <h3 className="font-semibold text-teal-800 mb-3 flex items-center gap-2">
              📋 Pre-filled Business Data (Copy & Paste Ready)
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center justify-between bg-white p-2 rounded">
                <span><strong>Name:</strong> {businessData.business_name}</span>
                <CopyButton text={businessData.business_name} label="name" copied={copied} onCopy={copyToClipboard} />
              </div>
              <div className="flex items-center justify-between bg-white p-2 rounded">
                <span><strong>Phone:</strong> {businessData.phone}</span>
                <CopyButton text={businessData.phone} label="phone" copied={copied} onCopy={copyToClipboard} />
              </div>
              <div className="flex items-center justify-between bg-white p-2 rounded">
                <span><strong>Website:</strong> {businessData.website}</span>
                <CopyButton text={businessData.website} label="website" copied={copied} onCopy={copyToClipboard} />
              </div>
              <div className="flex items-center justify-between bg-white p-2 rounded col-span-2">
                <span><strong>Address:</strong> {businessData.address}, {businessData.city}, {businessData.state} {businessData.zip_code}</span>
                <CopyButton text={`${businessData.address}, ${businessData.city}, ${businessData.state} ${businessData.zip_code}`} label="address" copied={copied} onCopy={copyToClipboard} />
              </div>
              <div className="flex items-center justify-between bg-white p-2 rounded">
                <span><strong>Email:</strong> {businessData.email}</span>
                <CopyButton text={businessData.email} label="email" copied={copied} onCopy={copyToClipboard} />
              </div>
            </div>
            <div className="mt-3">
              <button
                onClick={() => copyToClipboard(businessData.description_short, 'desc')}
                className="text-teal-700 hover:text-teal-800 text-sm flex items-center gap-1"
              >
                <Copy className="w-3 h-3" /> Copy Short Description
              </button>
            </div>
          </div>
        )}

        {/* Action Plan */}
        {actionPlan && (
          <div className="bg-white rounded-lg shadow mb-6">
            <button
              onClick={() => setShowActionPlan(!showActionPlan)}
              className="w-full p-4 flex items-center justify-between text-left"
            >
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                🎯 4-Week Citation Action Plan
              </h3>
              {showActionPlan ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
            
            {showActionPlan && (
              <div className="p-4 pt-0 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(actionPlan).filter(([key]) => key.startsWith('week')).map(([week, data]) => (
                  <div key={week} className="border rounded-lg p-3">
                    <h4 className="font-semibold text-sm text-gray-800 mb-1">{data.title}</h4>
                    <p className="text-xs text-gray-600 mb-2">{data.description}</p>
                    <div className="space-y-1">
                      {data.tasks?.slice(0, 3).map((task, idx) => (
                        <div key={idx} className="text-xs flex items-center gap-1">
                          {getStatusIcon(task.status)}
                          <span className="truncate">{task.name}</span>
                        </div>
                      ))}
                      {data.tasks?.length > 3 && (
                        <p className="text-xs text-gray-500">+{data.tasks.length - 3} more</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
            {['all', 'pending', 'submitted', 'live'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition ${
                  filter === f ? 'bg-teal-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
          
          <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm">
            {['all', 'aggregator', 'healthcare', 'directory', 'local'].map((c) => (
              <button
                key={c}
                onClick={() => setCategoryFilter(c)}
                className={`px-3 py-1.5 rounded text-sm font-medium transition flex items-center gap-1 ${
                  categoryFilter === c ? 'bg-purple-600 text-white' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {c !== 'all' && getCategoryIcon(c)}
                {c.charAt(0).toUpperCase() + c.slice(1)}
              </button>
            ))}
          </div>

          <button
            onClick={fetchData}
            className="ml-auto px-4 py-2 bg-white rounded-lg shadow-sm hover:bg-gray-50 flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        </div>

        {/* Directory List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading directories...</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredDirectories.map((dir) => (
              <div key={dir.id} className="bg-white rounded-lg shadow-sm border overflow-hidden">
                {/* Directory Header */}
                <div 
                  className="p-4 flex items-center justify-between cursor-pointer hover:bg-gray-50"
                  onClick={() => setExpandedId(expandedId === dir.id ? null : dir.id)}
                >
                  <div className="flex items-center gap-3">
                    {getStatusIcon(dir.status)}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{dir.name}</h3>
                        {dir.category === 'aggregator' && (
                          <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                            ⚡ Aggregator
                          </span>
                        )}
                        {dir.da_score >= 80 && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full">
                            DA {dir.da_score}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500">
                        {dir.feeds_to?.length > 0 && `Feeds to: ${dir.feeds_to.slice(0, 3).join(', ')}${dir.feeds_to.length > 3 ? '...' : ''}`}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(dir.status)}`}>
                      {dir.status.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500">{dir.cost}</span>
                    {expandedId === dir.id ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {expandedId === dir.id && (
                  <div className="border-t bg-gray-50 p-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Instructions */}
                      <div>
                        <h4 className="font-semibold text-sm mb-2">📝 Submission Steps:</h4>
                        <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                          {dir.instructions?.map((step, idx) => (
                            <li key={idx}>{step}</li>
                          ))}
                        </ol>
                        
                        <div className="mt-3 flex flex-wrap gap-2">
                          <a
                            href={dir.submission_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-teal-600 text-white rounded text-sm hover:bg-teal-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Submit Now
                          </a>
                          {dir.listing_url && (
                            <a
                              href={dir.listing_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
                            >
                              <ExternalLink className="w-4 h-4" />
                              View Listing
                            </a>
                          )}
                        </div>
                      </div>

                      {/* Details & Actions */}
                      <div>
                        <h4 className="font-semibold text-sm mb-2">ℹ️ Details:</h4>
                        <div className="space-y-1 text-sm text-gray-600">
                          <p><strong>Verification:</strong> {dir.verification_method}</p>
                          <p><strong>Est. Time:</strong> {dir.estimated_time}</p>
                          <p><strong>Cost:</strong> {dir.cost}</p>
                          {dir.notes && <p className="text-gray-500 italic">{dir.notes}</p>}
                        </div>

                        {/* Status Update Buttons */}
                        <div className="mt-4">
                          <h4 className="font-semibold text-sm mb-2">Update Status:</h4>
                          <div className="flex flex-wrap gap-2">
                            {['pending', 'submitted', 'live'].map((status) => (
                              <button
                                key={status}
                                onClick={() => updateStatus(dir.id, status)}
                                disabled={dir.status === status}
                                className={`px-3 py-1 rounded text-xs font-medium transition ${
                                  dir.status === status
                                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                    : 'bg-white border hover:bg-gray-50'
                                }`}
                              >
                                Mark {status.charAt(0).toUpperCase() + status.slice(1)}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Feeds To List */}
                    {dir.feeds_to?.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="font-semibold text-sm mb-2">🔗 This aggregator feeds data to:</h4>
                        <div className="flex flex-wrap gap-1">
                          {dir.feeds_to.map((site, idx) => (
                            <span key={idx} className="text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded">
                              {site}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {filteredDirectories.length === 0 && !loading && (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <p className="text-gray-500">No directories match your filters</p>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default CitationBuilder;
